import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeo } from '@/lib/seo';

const SECTIONS = [
  { n: 1, body: ['This policy sets out the returns policy for goods or services purchased through the online store operated by Motion Reality Sim Racing Ltd.'] },
  { n: 2, body: ['To be eligible for a return, your item must be unused, in the same condition that you received it, and in the original packaging.'] },
  { n: 3, body: [
    'Certain goods are exempt from being returned for a refund, unless they are unopened and unused. Custom-built or personalised sim rigs and components cannot be returned under any circumstances. Other exempt items include electronic products or software once opened or activated, consumable products such as cleaning fluids or lubricants, intimate or sanitary accessories like seat covers or harness padding, and hazardous materials or items containing flammable liquids or gases.',
  ] },
  { n: 4, body: [
    'Services that begin immediately upon payment, such as digital downloads, gift cards, or gift wrapping services, are non-refundable. By purchasing digital content, you agree to waive your cancellation rights once the download begins.',
  ] },
  { n: 5, body: [
    'If you change your mind within 14 calendar days of receiving your goods, you may return non-faulty items for a refund or exchange. A restocking fee of 10% of the product price applies to non-faulty returns, and you are responsible for the cost of return carriage unless the item is faulty or misdescribed. Your statutory cancellation rights under the Consumer Contracts Regulations 2013 — including the right to a full refund of the purchase price and standard delivery charges within the 14-day cooling-off period — are not affected.',
  ] },
  { n: 6, body: [
    'Our standard returns policy also allows you to request a refund within 14 calendar days of delivery if the goods are not faulty or misdescribed. Refunds will be issued within 14 days of receiving the returned goods.',
  ] },
  { n: 7, body: [
    'If the item you purchased was faulty or not as described, please contact us before requesting a refund. We may be able to resolve the issue without requiring a return.',
  ] },
  { n: 8, body: [
    'To return a physical product, please contact us in advance. For non-physical products (e.g. services), please contact us for instructions on how to request a refund.',
  ] },
  { n: 9, body: [
    'Once your return is received and inspected, we will notify you of the approval or rejection of your refund. Approved refunds will be credited to your original payment method within 14 days.',
  ] },
  { n: 10, body: [
    'If you have not received a refund after confirmation, please check your bank or card statement and contact your bank, as processing times may vary.',
  ] },
  { n: 11, body: [
    'If you used a discount voucher, your refund will reflect the amount paid, not the full listed price.',
  ] },
  { n: 12, body: [
    'Sale items are eligible for refunds unless otherwise stated. If you are unsure, please contact us before making a purchase.',
  ] },
  { n: 13, body: [
    'A model cancellation form is available upon request to assist with exercising your cancellation rights.',
  ] },
  { n: 14, body: [
    'We accept exchanges for non-faulty items returned within the 14-day window, subject to the same condition requirements. To request an exchange, submit a return request and select “Replacement”, or contact us to arrange a swap for an alternative product of equal or greater value (paying the difference where applicable).',
  ] },
  { n: 15, body: [
    'Returns can be made by mail or in person at our Bromborough showroom. Please contact us before sending any items back so we can confirm your return and provide the correct address and instructions. Return carriage is the customer’s responsibility unless the item is faulty or misdescribed.',
  ] },
  { n: 16, body: [
    'A restocking fee of 10% of the product price applies to non-faulty returns to cover inspection, testing and repackaging. This fee is deducted from your refund and does not apply to faulty, damaged or misdescribed items, nor does it affect your statutory cancellation rights.',
  ] },
];

const COMPANY = {
  name: 'Motion Reality Sim Racing Ltd',
  email: 'sales@motionreality.co.uk',
  phone: '0151 528 3001',
};

export default function RefundPolicy() {
  useSeo({
    title: 'Refund Policy — Motion Reality Sim Racing Ltd',
    description: 'Returns, cancellations and refund policy for goods and services purchased from Motion Reality Sim Racing Ltd.',
  });

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="py-16 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-primary text-xs font-bold tracking-widest mb-3">LEGAL</p>
            <h1 className="text-white font-black text-4xl md:text-5xl uppercase leading-tight mb-3">
              Refund Policy
            </h1>
            <p className="text-white/40 text-sm">
              Last updated: August 2026 · Returns, cancellations and refunds for purchases from Motion Reality Sim Racing Ltd.
            </p>
          </motion.div>
        </div>
      </section>

      {/* At a glance */}
      <section className="px-6 lg:px-10 py-12 border-b border-white/5">
        <div className="max-w-3xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/5 border border-white/5">
            {[
              { label: 'Returns', value: 'Defective & non-defective' },
              { label: 'Exchanges', value: 'Accepted' },
              { label: 'Condition', value: 'New / unused only' },
              { label: 'Window', value: '14 days from delivery' },
              { label: 'Method', value: 'By mail or in store' },
              { label: 'Return label', value: 'Customer responsibility' },
              { label: 'Restocking fee', value: '10% — non-faulty only' },
              { label: 'Refund time', value: 'Within 14 days' },
            ].map((row) => (
              <div key={row.label} className="bg-[#0d0d0f] p-4">
                <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">{row.label.toUpperCase()}</p>
                <p className="text-white/80 text-sm font-medium">{row.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact card */}
      <section className="px-6 lg:px-10 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="bg-[#111115] border border-white/5 p-7">
            <div className="flex items-center gap-2 mb-5">
              <Building2 className="w-4 h-4 text-primary" />
              <p className="text-primary text-[10px] font-bold tracking-widest">CONTACT US</p>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
              <div><dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">COMPANY</dt><dd className="text-white/80">{COMPANY.name}</dd></div>
              <div>
                <dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">EMAIL</dt>
                <dd><a href={`mailto:${COMPANY.email}`} className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{COMPANY.email}</a></dd>
              </div>
              <div>
                <dt className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">TELEPHONE</dt>
                <dd><a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="text-primary hover:text-primary/80 inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{COMPANY.phone}</a></dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      {/* Sections */}
      <section className="px-6 lg:px-10 pb-20">
        <div className="max-w-3xl mx-auto space-y-8">
          {SECTIONS.map((s) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-baseline gap-3">
                <span className="text-primary font-mono font-bold text-sm shrink-0">{String(s.n).padStart(2, '0')}</span>
                <div className="space-y-3">
                  {s.body.map((p, i) => (
                    <p key={i} className="text-white/55 text-sm leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 lg:px-10 pb-20">
        <div className="max-w-3xl mx-auto bg-[#111115] border border-white/5 p-8 text-center">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">NEED TO RETURN AN ITEM?</p>
          <h2 className="text-white font-black text-2xl uppercase mb-3">Start Your Return</h2>
          <p className="text-white/40 text-sm mb-6 max-w-md mx-auto">
            Please contact us before sending any items back so we can guide you through the process.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/returns" className="inline-block bg-primary hover:bg-primary/90 text-white font-bold text-sm px-7 py-3.5 transition-colors">
              START A RETURN
            </Link>
            <Link to="/contact" className="inline-block border border-white/15 hover:border-white/30 text-white/70 hover:text-white font-bold text-sm px-7 py-3.5 transition-colors">
              CONTACT US
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}